<template>
    <card plain class="m-0">
        <div class="template-info">
            <div>
                <strong>{{ faxTypeName }}</strong>
                <div class="text-muted small">
                    <span v-if="!value || !value.imageId" class="badge badge-secondary">Using Default</span>
                    <span v-else class="badge badge-primary">{{ value.fileName || 'Template Uploaded' }}</span>
                </div>
            </div>
            <div class="template-actions mt-2">
                <b-button
                    v-if="value && value.imageId"
                    variant="outline-danger"
                    size="sm"
                    @click="removeTemplate"
                    class="mr-2 mb-2">
                    <i class="fas fa-trash"></i>
                    Remove Override
                </b-button>
                <b-button
                    variant="primary"
                    size="sm"
                    :disabled="uploading"
                    @click="$refs.fileInput.click()"
                    class="mr-2 mb-2">
                    <i v-if="uploading" class="fas fa-spinner fa-spin"></i>
                    <i v-else class="fas fa-upload"></i>
                    {{ value && value.imageId ? 'Replace' : 'Upload' }} Template
                </b-button>
                <b-button
                    variant="primary"
                    size="sm"
                    @click="downloadDefaultTemplate"
                    class="mb-2">
                    <i class="fas fa-download"></i>
                    Download Default
                </b-button>
            </div>
        </div>

        <input
            ref="fileInput"
            type="file"
            accept=".docx"
            style="display: none"
            @change="handleFileSelection" />

        <div v-if="uploadProgress > 0 && uploadProgress < 100" class="mb-3">
            <b-progress :value="uploadProgress" :max="100" animated>
                <span>{{ Math.round(uploadProgress) }}%</span>
            </b-progress>
        </div>

        <div v-if="errorMessage" class="alert alert-danger">
            {{ errorMessage }}
        </div>

        <b-modal
            id="remove-template-modal"
            ref="removeModal"
            title="Remove Template Override"
            ok-title="Remove"
            ok-variant="danger"
            cancel-title="Cancel"
            @ok="confirmRemove">
            <p>Are you sure you want to remove this template override?</p>
            <p class="text-muted">The system will revert to using the default template.</p>
        </b-modal>
    </card>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import axios from 'axios';
import Card from '@/components/Cards/Card.vue';

interface FaxTemplateOverride {
    imageId: number;
    fileName?: string;
}

@Component({
    name: 'FaxTemplateUploadControl',
    components: {
        Card,
    },
})
export default class FaxTemplateUploadControl extends Vue {
    @Prop({ required: true })
    readonly faxType!: string;

    @Prop({ required: true })
    readonly faxTypeName!: string;

    @Prop({ required: false, default: null })
    readonly value!: FaxTemplateOverride | null;

    private uploading: boolean = false;
    private uploadProgress: number = 0;
    private errorMessage: string = '';

    $refs!: {
        fileInput: HTMLInputElement;
        removeModal: any;
    };

    private handleFileSelection(event: Event) {
        const target = event.target as HTMLInputElement;
        if (target.files && target.files.length > 0) {
            const file = target.files[0];
            this.uploadTemplate(file);
        }
    }

    private async uploadTemplate(file: File) {
        if (!file.name.toLowerCase().endsWith('.docx')) {
            this.errorMessage = 'Please select a valid .docx file';
            return;
        }

        this.errorMessage = '';
        this.uploadProgress = 0;
        this.uploading = true;

        const formData = new FormData();
        formData.append('files', file);

        try {
            const response = await axios.post('/api/Image/SaveDocument?bypassIngestion=true', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                        this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    }
                }
            });

            const imageIds = response.data as number[];
            if (imageIds && imageIds.length > 0) {
                const newValue: FaxTemplateOverride = {
                    imageId: imageIds[0],
                    fileName: file.name
                };
                this.$emit('input', newValue);
                this.uploadProgress = 0;
            } else {
                this.errorMessage = 'Upload failed: No image ID returned';
            }
        } catch (error) {
            if (axios.isAxiosError(error)) {
                if (error.response?.data) {
                    // Handle string or object error responses
                    const errorData = error.response.data;
                    if (typeof errorData === 'string') {
                        this.errorMessage = errorData;
                    } else if (errorData.message) {
                        this.errorMessage = errorData.message;
                    } else {
                        this.errorMessage = JSON.stringify(errorData);
                    }
                } else {
                    this.errorMessage = error.message || 'Failed to upload template';
                }
            } else {
                this.errorMessage = 'Failed to upload template';
            }
            this.uploadProgress = 0;
        } finally {
            this.uploading = false;
        }

        // Clear file input
        const fileInput = this.$refs.fileInput as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    }

    private removeTemplate() {
        this.$refs.removeModal.show();
    }

    private confirmRemove() {
        this.$emit('input', null);
    }

    private async downloadDefaultTemplate() {
        try {
            const response = await axios.get(`/api/Fax/template-default/${this.faxType}`, {
                responseType: 'blob'
            });

            // Create a blob URL and trigger download
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Extract filename from Content-Disposition header or use default
            const contentDisposition = response.headers['content-disposition'];
            let fileName = `default_${this.faxType}Template.docx`;
            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (fileNameMatch && fileNameMatch[1]) {
                    fileName = fileNameMatch[1].replace(/['"]/g, '');
                }
            }

            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                this.errorMessage = error.response?.data?.message || 'Failed to download default template';
            } else {
                this.errorMessage = 'Failed to download default template';
            }
        }
    }
}
</script>

<style scoped>
.template-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0;
}
</style>
